import { useState, useEffect } from 'react';
import { Users, DollarSign, TrendingUp, TrendingDown, Target, PieChart } from 'lucide-react';
import { familyService } from '@services/familyService';
import { useCategories } from '@hooks/useCategories';
import { formatCurrency } from '@utils/formatters';
import Loading from '@components/common/Loading';

const getInitials = (name = '', email = '') => {
  if (name) return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  return email.slice(0, 2).toUpperCase();
};

const FamilyDashboard = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { categories } = useCategories();

  useEffect(() => {
    const load = async () => {
      try {
        const result = await familyService.getDashboard();
        setData(result);
      } catch (e) {
        console.error('Erro ao carregar dashboard familia', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  if (isLoading) return <Loading text="Carregando dashboard da familia..." />;
  if (!data) return null;

  const { summary, members, sharedGoals, topCategories } = data;

  const getCategoryName = (categoryId) => {
    const cat = (categories || []).find(c => c.categoryId === categoryId);
    return cat ? `${cat.icon} ${cat.name}` : categoryId;
  };

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
              <Users size={18} className="text-blue-600" />
            </div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Membros</p>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{summary.memberCount}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl">
              <TrendingUp size={18} className="text-emerald-600" />
            </div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Receitas do Mes</p>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{formatCurrency(summary.totalIncome)}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-xl">
              <TrendingDown size={18} className="text-red-600" />
            </div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Despesas do Mes</p>
          </div>
          <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.totalExpense)}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
              <DollarSign size={18} className="text-primary-600" />
            </div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Saldo Compartilhado</p>
          </div>
          <p className={`text-2xl font-bold ${summary.sharedBalance >= 0 ? 'text-primary-600' : 'text-red-600'}`}>
            {formatCurrency(summary.sharedBalance)}
          </p>
        </div>
      </div>

      {/* Member activity */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="p-5 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Users size={18} className="text-primary-600" />
            Atividade por Membro
          </h3>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {members.map(member => (
            <div key={member.userId} className="flex items-center gap-4 p-5">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
              >
                {getInitials(member.name, member.email)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{member.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {member.transactionCount} transacoes este mes
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-emerald-600">+{formatCurrency(member.monthlyIncome)}</p>
                <p className="text-sm font-semibold text-red-600">-{formatCurrency(member.monthlyExpense)}</p>
              </div>
            </div>
          ))}
          {members.length === 0 && (
            <div className="p-8 text-center text-sm text-gray-500">Nenhum membro encontrado</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Shared goals */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="p-5 border-b border-gray-100 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Target size={18} className="text-purple-600" />
              Metas Compartilhadas
            </h3>
          </div>
          <div className="p-5 space-y-4">
            {sharedGoals.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                Nenhuma meta compartilhada. Crie uma em Sonhos e Metas.
              </p>
            ) : (
              sharedGoals.map(goal => (
                <div key={goal.goalId} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{goal.icon}</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{goal.name}</span>
                    </div>
                    <span className="text-xs font-semibold text-primary-600">{goal.progress.toFixed(0)}%</span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary-500 to-purple-500 transition-all"
                      style={{ width: `${goal.progress}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{formatCurrency(goal.currentAmount)}</span>
                    <span>{formatCurrency(goal.targetAmount)}</span>
                  </div>
                  {goal.contributions.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {goal.contributions.slice(-3).map((c, i) => (
                        <span key={i} className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full">
                          {c.userName}: {formatCurrency(c.amount)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Top expense categories */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="p-5 border-b border-gray-100 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <PieChart size={18} className="text-orange-600" />
              Top Gastos da Familia
            </h3>
          </div>
          <div className="p-5 space-y-3">
            {topCategories.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                Nenhum gasto registrado este mes
              </p>
            ) : (
              topCategories.map((cat, i) => {
                const maxAmount = topCategories[0]?.amount || 1;
                const pct = (cat.amount / maxAmount) * 100;
                return (
                  <div key={cat.categoryId} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 dark:text-gray-300">{getCategoryName(cat.categoryId)}</span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(cat.amount)}</span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
                      <div
                        className="h-full rounded-full bg-orange-400"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamilyDashboard;
